import React from 'react';
import { Box as MuiBox, BoxProps as MuiBoxProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import withAutoHeadingLevel from '../../utils/withAutoHeadingLevel';

export interface SectionProps extends Omit<MuiBoxProps, keyof PounceStyleProps>, PounceStyleProps {
  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiBoxProps['sx'];
}

/**
 * Section renders a `<section>` tag and should be used whenever you need this specific semantic.
 *
 * The core benefit of this component is the fact that it automatically increases the heading level of the
 * [Heading](../?path=/general-text-heading--docs) components inside it by 1. See the examples below to better understand
 * this.
 *
 * Provides the same API as the original pounce Section component, allowing for easy migration
 * from the original pounce library. All pounce-style props are automatically converted
 * to MUI sx props while maintaining theme integration.
 *
 * **NOTE**: this component is functionally the same as [Article](../?path=/general-layout-article--docs), but semantically
 * it renders a `<section />` tag instead of an `<article />` tag.
 */
const SectionBase = React.forwardRef<HTMLElement, SectionProps>(function Section(
  { children, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with user-provided sx
  const combinedSx = { ...pounceStyles, ...sx };

  return (
    <MuiBox ref={ref} component="section" sx={combinedSx} {...otherProps}>
      {children}
    </MuiBox>
  );
});

export const Section = withAutoHeadingLevel()(SectionBase) as React.FC<SectionProps>;
